import { useEffect, useState, type ButtonHTMLAttributes, type ReactNode } from "react";
import { Link } from "react-router";
import { ApiError } from "../api.js";

const STATUS_TONE: Record<string, "ok" | "err" | "warn" | "run" | "muted"> = {
  succeeded: "ok",
  approved: "ok",
  available: "ok",
  idle: "ok",
  success: "ok",
  failed: "err",
  rejected: "err",
  denied: "err",
  offline: "err",
  failure: "err",
  "pending-approval": "warn",
  "awaiting-approval": "warn",
  busy: "run",
  running: "run",
  assigned: "run",
  queued: "muted",
  pending: "muted",
  created: "muted",
  blocked: "muted",
  cancelled: "muted",
  skipped: "muted",
  retired: "muted",
};

export function StatusBadge({ status }: { status: string }) {
  const tone = STATUS_TONE[status] ?? "muted";
  return (
    <span className={`badge badge-${tone}`} data-status={status}>
      {tone === "run" && <span className="pulse" aria-hidden />}
      {status.replace(/-/g, " ")}
    </span>
  );
}

export function relativeTime(iso: string, now = Date.now()): string {
  const seconds = Math.round((now - Date.parse(iso)) / 1000);
  if (seconds < 45) return "just now";
  if (seconds < 3600) return `${Math.round(seconds / 60)}m ago`;
  if (seconds < 86_400) return `${Math.round(seconds / 3600)}h ago`;
  if (seconds < 30 * 86_400) return `${Math.round(seconds / 86_400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

export function Time({ iso }: { iso: string | null | undefined }) {
  const [, tick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => {
      tick((n) => n + 1);
    }, 30_000);
    return () => {
      clearInterval(t);
    };
  }, []);
  if (!iso) return <span className="muted">—</span>;
  return (
    <time dateTime={iso} title={new Date(iso).toLocaleString()}>
      {relativeTime(iso)}
    </time>
  );
}

export function duration(from: string | null | undefined, to: string | null | undefined): string {
  if (!from) return "—";
  const ms = (to ? Date.parse(to) : Date.now()) - Date.parse(from);
  const s = Math.max(0, Math.round(ms / 1000));
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
}

export function Sha({ sha }: { sha: string }) {
  return (
    <code className="sha" title={sha}>
      {sha.slice(0, 7)}
    </code>
  );
}

export function ErrorBox({ error }: { error: Error | undefined }) {
  if (!error) return null;
  return (
    <div className="error-box" role="alert">
      <strong>{error instanceof ApiError ? error.code : "Error"}</strong> {error.message}
    </div>
  );
}

export function Loading({ label = "Loading…" }: { label?: string }) {
  return (
    <div className="loading" role="status">
      <span className="spinner" aria-hidden /> {label}
    </div>
  );
}

export function Empty({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="empty">
      <p className="empty-title">{title}</p>
      {children && <div className="muted">{children}</div>}
    </div>
  );
}

export function Button({ variant = "default", busy, children, ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: "default" | "primary" | "danger"; busy?: boolean }) {
  return (
    <button {...rest} className={`btn btn-${variant} ${rest.className ?? ""}`} disabled={rest.disabled === true || busy === true}>
      {busy && <span className="spinner" aria-hidden />}
      {children}
    </button>
  );
}

export function Card({ title, actions, children }: { title?: ReactNode; actions?: ReactNode; children: ReactNode }) {
  return (
    <section className="card">
      {(title !== undefined || actions !== undefined) && (
        <header className="card-header">
          {title !== undefined && <h2>{title}</h2>}
          {actions !== undefined && <div className="card-actions">{actions}</div>}
        </header>
      )}
      {children}
    </section>
  );
}

export function Tabs({ tabs, active, base }: { tabs: { id: string; label: string }[]; active: string; base: string }) {
  return (
    <nav className="tabs" aria-label="Sections">
      {tabs.map((t) => (
        <Link key={t.id} to={t.id === tabs[0]?.id ? base : `${base}/${t.id}`} className={t.id === active ? "tab active" : "tab"} aria-current={t.id === active ? "page" : undefined}>
          {t.label}
        </Link>
      ))}
    </nav>
  );
}

export function PageHeader({ title, subtitle, actions }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode }) {
  return (
    <header className="page-header">
      <div>
        <h1>{title}</h1>
        {subtitle !== undefined && <p className="muted">{subtitle}</p>}
      </div>
      {actions !== undefined && <div className="page-actions">{actions}</div>}
    </header>
  );
}

/** Runs an async action with busy and error state. */
export function useAction<A extends unknown[]>(action: (...args: A) => Promise<unknown>, onDone?: () => void) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error>();
  const run = async (...args: A): Promise<void> => {
    setBusy(true);
    setError(undefined);
    try {
      await action(...args);
      onDone?.();
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, run };
}
